import React, { useState, useEffect } from "react";
import { Search, CheckCircle, XCircle, Eye } from "lucide-react";
import axios from "axios";
import { BASE_API_URL } from "../../../data/api";

const statusColor = {
  Pending: "bg-yellow-100 text-yellow-700",
  Requested: "bg-yellow-100 text-yellow-700",
  Completed: "bg-green-100 text-green-700",
  Rejected: "bg-red-100 text-red-700",
  Failed: "bg-red-100 text-red-700",
};

const Return = () => {
  const [search, setSearch] = useState("");
  const [returnOrders, setReturnOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const vendorId = localStorage.getItem("vendorId");

  useEffect(() => {
    const fetchReturnOrders = async () => {
      try {
        setLoading(true);
        const url = vendorId
          ? `${BASE_API_URL}/Order/vendor/${vendorId}`
          : `${BASE_API_URL}/Order/return-requests`;
        const response = await axios.get(url);
        setReturnOrders(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Error fetching return orders:", error);
        setReturnOrders([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReturnOrders();
  }, [vendorId]);

  const filteredOrders = returnOrders.filter((o) => {
    // Search by OrderId or ReturnStatus
    return (
      o.orderId.toString().includes(search) ||
      o.returnStatus.toLowerCase().includes(search.toLowerCase())
    );
  });

  const handleReturnAction = async (orderId, action) => {
    // Approve = "Completed", Reject = "Rejected"
    const status = action === "approve" ? "Completed" : "Rejected";
    try {
      const response = await axios.put(`${BASE_API_URL}/Order/return/${orderId}`, {
        returnStatus: status,
        returnReason: "Handled by vendor", // optional reason
      });
      setReturnOrders((prev) =>
        prev.map((o) => (o.orderId === orderId ? response.data.order : o))
      );
    } catch (err) {
      console.error("Error updating return:", err);
    }
  };

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">Return Requests</h1>
      <p className="mb-4 text-gray-600 font-medium">
        Total Returns: {returnOrders.length}
      </p>

      {/* Search */}
      <div className="flex items-center gap-2 mb-4 bg-white rounded-xl shadow p-3 w-full md:w-1/3">
        <Search className="text-gray-500" />
        <input
          type="text"
          placeholder="Search by ID or status..."
          className="w-full outline-none"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Table */}
      <div className="bg-white shadow rounded-xl p-4 overflow-x-auto">
        {loading ? (
          <p className="text-center text-gray-500 p-4">Loading return requests...</p>
        ) : filteredOrders.length > 0 ? (
          <table className="w-full">
            <thead>
              <tr className="text-left border-b">
                <th className="p-3">Return ID</th>
                <th className="p-3">Customer ID</th>
                <th className="p-3">Amount</th>
                <th className="p-3">Request Date</th>
                <th className="p-3">Status</th>
                <th className="p-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <tr key={order.orderId} className="border-b hover:bg-gray-50">
                  <td className="p-3 font-medium">{order.orderId}</td>
                  <td className="p-3">{order.userId}</td>
                  <td className="p-3">₹{order.finalAmount}</td>
                  <td className="p-3">
                    {order.createdAt
                      ? new Date(order.createdAt).toLocaleDateString()
                      : "N/A"}
                  </td>
                  <td className="p-3">
                    <span
                      className={`px-3 py-1 rounded-full text-sm ${
                        statusColor[order.returnStatus] || "bg-gray-100 text-gray-600"
                      }`}
                      title={order.returnReason}
                    >
                      {order.returnStatus}
                    </span>
                  </td>
                  <td className="p-3 flex items-center justify-center gap-3">
                    <button
                      onClick={() => handleReturnAction(order.orderId, "approve")}
                      className="p-2 rounded-full bg-green-100 text-green-700 hover:bg-green-200"
                    >
                      <CheckCircle size={18} />
                    </button>
                    <button
                      onClick={() => handleReturnAction(order.orderId, "reject")}
                      className="p-2 rounded-full bg-red-100 text-red-700 hover:bg-red-200"
                    >
                      <XCircle size={18} />
                    </button>
                    <button className="p-2 rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200">
                      <Eye size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-center text-gray-500 p-4">No return requests found...</p>
        )}
      </div>
    </div>
  );
};

export default Return;
